import { useContext, type FC } from 'react';
import { Globe, ArrowRightLeft, AtSign, Wrench, Sparkles, Network } from 'lucide-react';
import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { arr, str } from '@/app/lib/a2ui/props.ts';
import { isRecord } from '@/app/lib/util/type-guards.ts';
import { A2uiSurfaceContext } from '@/app/lib/a2ui/surface-context.ts';
import { MarkdownText } from '@/app/lib/a2ui/blocks/MarkdownText.tsx';
import { SuggestedReply } from '@/app/lib/a2ui/blocks/SuggestedReply.tsx';
import { ExploreContent } from '@/app/lib/a2ui/ExploreContent.tsx';
import { SurfaceHeadingLevel } from '@/app/lib/a2ui/blocks/heading-context.ts';

export const SupportGuide: FC<A2uiNodeViewProps> = ({ renderChildren }) => <>{renderChildren()}</>;
const icons = { website: Globe, move: ArrowRightLeft, domain: Network, email: AtSign, repair: Wrench, ai: Sparkles };
export const SupportGuideContent: FC<A2uiNodeViewProps> = ({ node }) => {
  const p = node.props;
  const api = useContext(A2uiSurfaceContext);
  const level = useContext(SurfaceHeadingLevel);
  const Heading = level === 1 ? 'h1' : 'h2';
  return <article className="@container/support mx-auto w-full max-w-[880px] min-w-0 space-y-8 py-5 text-foreground">
    {str(p.highlight) && <Heading className="max-w-[780px] text-balance text-4xl font-semibold leading-[1.12] tracking-tight @2xl/support:text-5xl">{str(p.highlight)}</Heading>}
    {str(p.message) && <MarkdownText text={str(p.message)} className="max-w-[600px] text-lg leading-relaxed" />}
    {arr(p.services).length > 0 && <div className="grid grid-cols-1 gap-4 @2xl/support:grid-cols-2">
      {arr(p.services).filter(isRecord).map((s, i) => {
        const Icon = icons[str(s.icon) as keyof typeof icons] || Globe;
        return <ExploreContent key={str(s.id) || i} node={node} subject={str(s.title)} selection={{ id: s.id, title: s.title, explanation: s.explanation }} className="min-w-0 rounded-2xl border border-border bg-card p-6 text-card-foreground hover:border-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
          <Icon className="mb-5 h-6 w-6 text-primary" aria-hidden="true" />
          <h3 className="mb-2 break-words text-xl font-semibold">{str(s.title)}</h3>
          <p className="break-words text-base leading-relaxed text-muted-foreground">{str(s.explanation)}</p>
        </ExploreContent>;
      })}
    </div>}
    {(str(p.question) || arr(p.replies).length > 0) && <div className="space-y-4 border-t border-border pt-6">
      {str(p.question) && <p className="max-w-[600px] text-lg leading-relaxed">{str(p.question)}</p>}
      {arr(p.replies).length > 0 && <div className="flex flex-wrap gap-3">{arr(p.replies).filter(isRecord).map((r, i) => <SuggestedReply key={str(r.id) || i} disabled={!api || !str(r.id) || !str(r.label)} onClick={() => api?.dispatch('askSupport', { replyId: str(r.id) }, str(r.label))}>{str(r.label)}</SuggestedReply>)}</div>}
    </div>}
  </article>;
};
export const IntakeDraftContent: FC<A2uiNodeViewProps> = ({ node }) => {
  const p = node.props;
  return <article className="mx-auto w-full max-w-[780px] min-w-0 space-y-7 py-5 text-foreground">
    <aside className="space-y-2 rounded-xl border border-warning/50 bg-card p-5 text-base">
      <strong className="text-warning">{str(p.testLabel)}</strong>
      <p className="text-muted-foreground">{str(p.disclosure)}</p>
    </aside>
    {str(p.highlight) && <h2 className="text-balance text-3xl font-semibold tracking-tight">{str(p.highlight)}</h2>}
    <dl className="divide-y divide-border">{arr(p.details).filter(isRecord).map((d, i) => <div key={str(d.id) || i} className="grid gap-2 py-4 sm:grid-cols-[150px_1fr]">
      <dt className="text-base text-muted-foreground">{str(d.label)}</dt><dd className="min-w-0 break-words text-base">{str(d.value)}</dd>
    </div>)}</dl>
    {str(p.message) && <MarkdownText text={str(p.message)} className="max-w-[600px] text-lg leading-relaxed" />}
  </article>;
};
